import { useCallback, useState } from 'react'
import {
  AppShell,
  Button,
  DocumentsView,
  PromptEditor,
  PromptsView,
  SettingsView,
  SkillsView,
  WhatsNewDialog,
  useWhatsNew,
  type DocFile,
  type PromptDraft,
  type PromptItem,
  type SettingsViewProps,
  type Tab,
} from '@saip/ui'
import { useChat, useDocuments, usePrompts, useSkills } from '@saip/core'
import type { DocumentRef } from '@saip/backend'
import { ChatView } from './ChatView'

type Sub =
  null | { kind: 'settings' } | { kind: 'skills' } | { kind: 'prompt-editor'; prompt?: PromptItem }

export interface AppProps {
  /** LiteLLM connection wiring, owned by <Root/> and rendered in the settings. */
  settings: SettingsViewProps
}

export function App({ settings }: AppProps) {
  const chat = useChat()
  const promptsApi = usePrompts()
  const skillsApi = useSkills()
  const docs = useDocuments()

  // Dev convenience: ?screen=prompts|documents|settings|skills deep-links a view (for visual QA).
  const screen0 = new URLSearchParams(window.location.search).get('screen')
  const [tab, setTab] = useState<Tab>(
    screen0 === 'prompts' || screen0 === 'documents' ? screen0 : 'chat',
  )
  const [sub, setSub] = useState<Sub>(
    screen0 === 'settings'
      ? { kind: 'settings' }
      : screen0 === 'skills'
        ? { kind: 'skills' }
        : null,
  )
  const [showHistory, setShowHistory] = useState(false)
  const [promptQuery, setPromptQuery] = useState('')
  const [promptTag, setPromptTag] = useState<string | null>(null)
  const [docQuery, setDocQuery] = useState('')
  // Prompt picked via "Verwenden" → inserted into the chat composer.
  const [pendingInsert, setPendingInsert] = useState<string | null>(null)
  const clearInsert = useCallback(() => setPendingInsert(null), [])
  // Shows the release notes once per version, over whichever screen is up.
  const whatsNew = useWhatsNew()

  const screen = () => {
    // ---- Sub-screens (back chevron, no tabs) ----
    if (sub?.kind === 'settings') {
      return (
        <AppShell back={{ title: 'Einstellungen', onBack: () => setSub(null) }}>
          <SettingsView {...settings} />
        </AppShell>
      )
    }

    if (sub?.kind === 'skills') {
      return (
        <AppShell
          back={{
            title: 'Skills',
            onBack: () => setSub(null),
            action: (
              <Button variant="primary" size="sm">
                + Neu
              </Button>
            ),
          }}
        >
          <SkillsView skills={skillsApi.skills} onToggle={skillsApi.toggle} />
        </AppShell>
      )
    }

    if (sub?.kind === 'prompt-editor') {
      const picked = sub.prompt
      // The card (PromptItem) doesn't carry the body — resolve the full template by id.
      const full = picked ? promptsApi.prompts.find((p) => p.id === picked.id) : undefined
      const initial: Partial<PromptDraft> | undefined = picked
        ? {
            id: picked.id,
            name: picked.name,
            command: picked.command ?? '',
            description: picked.description ?? '',
            body: full?.body ?? '',
            tags: picked.tags ?? [],
            source: picked.source ?? 'local',
          }
        : undefined
      return (
        <AppShell back={{ title: 'Prompt bearbeiten', onBack: () => setSub(null) }}>
          <PromptEditor
            initial={initial}
            onCancel={() => setSub(null)}
            onSave={async (d) => {
              if (d.id) {
                await promptsApi.save({
                  id: d.id,
                  name: d.name,
                  command: d.command,
                  description: d.description,
                  body: d.body,
                  tags: d.tags,
                  source: d.source,
                  version: 1,
                })
              } else {
                await promptsApi.save({
                  name: d.name,
                  command: d.command,
                  description: d.description,
                  body: d.body,
                  tags: d.tags,
                  source: d.source,
                })
              }
              setSub(null)
            }}
          />
        </AppShell>
      )
    }

    // ---- Tab mode ----
    const tabActions =
      tab === 'chat' ? undefined : (
        <Button
          variant="primary"
          size="sm"
          onClick={() =>
            tab === 'prompts'
              ? setSub({ kind: 'prompt-editor' })
              : void docs.createCollection('Neuer Pool')
          }
        >
          + Neu
        </Button>
      )

    return (
      <AppShell
        tab={tab}
        onTab={setTab}
        onHistory={() => setShowHistory((v) => !v)}
        onNewChat={() => {
          chat.reset()
          setShowHistory(false)
        }}
        onSettings={() => setSub({ kind: 'settings' })}
        tabActions={tabActions}
      >
        {tab === 'chat' && (
          <ChatView
            chat={chat}
            prompts={promptsApi.prompts}
            showHistory={showHistory}
            onCloseHistory={() => setShowHistory(false)}
            insert={pendingInsert}
            onInsertConsumed={clearInsert}
          />
        )}

        {tab === 'prompts' && (
          <PromptsView
            prompts={promptsApi.prompts}
            query={promptQuery}
            onQuery={setPromptQuery}
            activeTag={promptTag}
            onTag={setPromptTag}
            onUse={(p) => {
              setPendingInsert(p.command ?? `/${p.id}`)
              setTab('chat')
            }}
            onEdit={(p) => setSub({ kind: 'prompt-editor', prompt: p })}
          />
        )}

        {tab === 'documents' && (
          <DocumentsView
            pools={docs.collections}
            filesByPool={mapFiles(docs.docsByCollection)}
            isActive={(id) => docs.active.has(id)}
            openDocName={docs.openDocName}
            query={docQuery}
            onQuery={setDocQuery}
            onToggleActive={docs.toggleActive}
            onAddDocument={() => {}}
            onCreatePool={() => void docs.createCollection('Neuer Pool')}
          />
        )}
      </AppShell>
    )
  }

  return (
    <>
      {screen()}
      {whatsNew.open && <WhatsNewDialog onClose={whatsNew.dismiss} />}
    </>
  )
}

function mapFiles(byCollection: Record<string, DocumentRef[]>): Record<string, DocFile[]> {
  const out: Record<string, DocFile[]> = {}
  for (const [collectionId, list] of Object.entries(byCollection)) {
    out[collectionId] = list.map((d) => ({
      id: d.id,
      name: d.name,
      sections: typeof d.metadata?.sections === 'number' ? d.metadata.sections : undefined,
    }))
  }
  return out
}
